import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import { useKeyPressed } from '../../hooks';
import { roundTo as roundToFunc } from '../../utilities';

export type InputProps = {
  name?: string;
  label?: React.ReactNode;
  className?: string;
  numeric?: boolean;
  roundTo?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  readonly?: boolean;
  autoFocus?: boolean;
  withBorder?: boolean;
  inputClassName?: string;
  borderClassName?: string;
  defaultSizeClass?: string;
  placeholder?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  type?: any;
  value?: string | number;

  rows?: number;
  multiline?: boolean;

  isChild?: boolean;
  rounded?: boolean;
  inputRoundedClassName?: string;

  labelClassName?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  onSubmit?: (newValue: string) => void;
  onChange?: (newValue: string) => void;
};

const useFocusAndSubmit = ({
  value,
  autoFocus,
  disabled,
  onBlur,
  onFocus,
  onSubmit,
}: Pick<
  InputProps,
  'autoFocus' | 'value' | 'disabled' | 'onBlur' | 'onFocus' | 'onSubmit'
>) => {
  const ref = useRef<any>(null);
  const [focus, setFocus] = useState(false);

  useEffect(() => {
    if (!autoFocus) return;

    ref.current?.focus();
  }, [autoFocus]);

  useKeyPressed(
    'Enter',
    e => {
      if (e.shiftKey) return;

      e.preventDefault();
      e.stopPropagation();

      onSubmit?.(String(value ?? ''));
    },
    !!onSubmit && !disabled && !!focus
  );

  return {
    ref,
    handleBlur: () => {
      setFocus(false);
      onBlur?.();
    },
    handleFocus: () => {
      setFocus(true);
      onFocus?.();
    },
  };
};

const Input: React.FC<InputProps> = ({
  name,
  label,
  prefix,
  suffix,
  className,
  inputClassName = 'text-text text-medium',
  rounded = true,
  inputRoundedClassName = 'rounded-lg',
  onBlur,
  onFocus,
  onChange,
  onSubmit,
  min,
  max,
  type,
  value = '',
  placeholder,
  roundTo = 2,
  isChild = false,
  numeric = false,
  disabled = false,
  readonly = false,
  autoFocus = false,
  withBorder = true,

  rows = 2,
  multiline = false,

  borderClassName = 'shadow-sm border border-active',
  defaultSizeClass = 'min-w-[14rem] md:min-w-[20rem] grow',
  labelClassName = 'text-text text-small font-normal',
}) => {
  const { ref, handleBlur, handleFocus } = useFocusAndSubmit({
    value,
    autoFocus,
    disabled,
    onBlur,
    onFocus,
    onSubmit,
  });

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }
  };
  const handleWheel = (
    e: React.UIEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    e.stopPropagation();
    e.currentTarget.blur();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;

    if (!numeric || roundTo === undefined) return onChange?.(value);

    const roundedNumber = String(roundToFunc(value, roundTo));
    return onChange?.(roundedNumber);
  };

  const Component = multiline ? 'textarea' : 'input';

  return (
    <div
      className={clsx(
        'flex flex-col items-start relative h-fit',
        className,
        defaultSizeClass
      )}
    >
      {!!label && <div className={labelClassName}>{label}</div>}
      {!!prefix && (
        <div className="absolute left-4 h-full top-0 inline-flex items-center justify-center">
          {prefix}
        </div>
      )}
      {!!suffix && (
        <div className="absolute right-4 h-full top-0 inline-flex items-center justify-center">
          {suffix}
        </div>
      )}

      <Component
        name={name}
        ref={multiline ? textAreaRef : ref}
        min={min}
        max={max}
        value={value ?? ''}
        readOnly={readonly}
        onBlur={e => {
          handleBlur?.();
          if (!numeric || disabled) return;

          const newValue = e.target.value.replace(/[^0-9]$/, '');
          onChange?.(newValue);
          ref.current.value = newValue;
        }}
        onFocus={handleFocus}
        placeholder={placeholder}
        type={numeric ? 'number' : type}
        onWheel={numeric ? handleWheel : undefined}
        onScroll={numeric ? handleWheel : undefined}
        onChange={disabled ? () => {} : handleChange}
        onKeyDown={numeric ? handleKeyDown : undefined}
        className={clsx(
          disabled ? 'opacity-50 pointer-events-none' : '',
          readonly && !isChild ? 'bg-gray-100' : '',
          multiline
            ? 'overflow-y-auto resize-none p-lg'
            : 'overflow-hidden p-sm px-md',
          'bg-content outline-none w-full appearance-none',
          inputClassName,
          rounded ? inputRoundedClassName : '',
          withBorder ? borderClassName : ''
        )}
        {...(multiline ? { rows } : {})}
      />
    </div>
  );
};

export default React.memo(Input) as typeof Input;
